import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || '';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check authentication status on mount (skip on verify page - it handles its own auth)
  useEffect(() => {
    if (window.location.pathname.startsWith('/auth/verify')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Try to get current user (will use cookies automatically)
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else if (response.status === 401) {
        // Try to refresh the token
        const refreshed = await refreshToken();
        if (!refreshed) {
          setUser(null);
        }
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        return true;
      }
    } catch (err) {
      console.error('Token refresh failed:', err);
    }
    return false;
  };

  const requestMagicLink = async (email) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/request-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send login link');
      }

      return { success: true, message: data.message };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const verifyMagicLink = async (token) => {
    setError(null);
    try {
      console.log('Verifying magic link token...');
      const response = await fetch(`${API_URL}/api/auth/verify?token=${token}`, {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });

      console.log('Verify response status:', response.status);
      const contentType = response.headers.get('content-type');
      console.log('Response content-type:', contentType);

      // Check if response is JSON
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        throw new Error('Server returned an invalid response. Please try again.');
      }

      const data = await response.json();
      console.log('Verify response data:', data);

      if (!response.ok) {
        throw new Error(data.error || 'Invalid login link');
      }

      setUser(data.user);
      return { success: true, redirectUrl: data.redirectUrl };
    } catch (err) {
      console.error('Magic link verification error:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
    }
  }, []);

  // Auto-refresh token before expiry (every 12 minutes)
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      refreshToken();
    }, 12 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    requestMagicLink,
    verifyMagicLink,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
